import { useState } from 'react'
import { Plus } from 'lucide-react'
import { Badge, Can, KeyValueList, notify } from '@/kit/ui'
import { money } from '@/lib/lender'
import { useCurrentUser } from '@/shell/useCurrentUser'
import { useHubStore } from '@/store/hubStore'
import type { Lender, Product } from '@/types'
import { ProductDrawer } from './ProductDrawer'

/** Tab 2 · Products & key facts (F1-5): one card per product, its Active switch, Edit and Add drawers. */
export function ProductsTab({ lender: l }: { lender: Lender }) {
  const { user, can } = useCurrentUser()
  const updateProduct = useHubStore((s) => s.updateProduct)
  const [editing, setEditing] = useState<Product | 'new' | null>(null)

  const toggle = (p: Product) => {
    updateProduct(l.slug, p.slug, { active: !p.active }, user.email, p.active ? 'Set inactive (hidden everywhere; endpoint answers product_unavailable)' : 'Set active')
    notify[p.active ? 'warning' : 'success'](`${p.name} ${p.active ? 'inactive' : 'active'}`, p.active ? 'Hidden on the website and in the app within 60 s.' : 'Shown again within 60 s.')
  }

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted">One card per product. Key facts show word for word on the website and in the app. The lender switch overrides every product switch.</p>
        <Can permission="lenders.edit">
          <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}><Plus size={12} /> Add product</button>
        </Can>
      </div>
      <div className="space-y-3">
        {l.products.map((p) => (
          <section key={p.slug} className="card">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="section-title !mb-0">{p.name}</h2>
                <div className="mono text-muted">
                  {p.slug} · {p.endpointId ? <>endpoint <b>{p.endpointId}</b> ({p.endpointState === 'live' ? 'live' : p.endpointState === 'uat' ? 'UAT only' : 'none'})</> : 'information only (no endpoint)'}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  role="switch"
                  aria-checked={p.active}
                  aria-label={`${p.name}: ${p.active ? 'active' : 'inactive'}`}
                  disabled={!can('lenders.edit')}
                  onClick={() => toggle(p)}
                  className="inline-flex items-center gap-2 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span className={`relative h-5 w-9 shrink-0 rounded-full transition ${p.active ? 'bg-primary' : 'bg-red-500'}`}>
                    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition ${p.active ? 'left-[18px]' : 'left-0.5'}`} />
                  </span>
                  {p.active ? 'Active' : 'Inactive'}
                </button>
                <Can permission="lenders.edit">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(p)}>Edit</button>
                </Can>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-x-6 lg:grid-cols-2">
              <KeyValueList
                columns={1}
                items={[
                  { label: 'Loan amount', value: `${money(p.keyFacts.amountMin)} – ${money(p.keyFacts.amountMax)}` },
                  { label: 'Tenure', value: `${p.keyFacts.tenureMinMonths} – ${p.keyFacts.tenureMaxMonths} months` },
                  { label: 'Interest rate', value: `${p.keyFacts.rateMinPa}% – ${p.keyFacts.rateMaxPa}% p.a.` },
                  { label: 'Processing fee', value: p.keyFacts.processingFee || null },
                  { label: 'APR range', value: `${p.keyFacts.aprMin}% – ${p.keyFacts.aprMax}%` },
                  { label: 'Age', value: `${p.keyFacts.ageMin} – ${p.keyFacts.ageMax} years` },
                ]}
              />
              <KeyValueList
                columns={1}
                items={[
                  { label: 'Minimum income', value: p.keyFacts.minIncomeMonth ? `${money(p.keyFacts.minIncomeMonth)} / month` : null },
                  { label: 'Documents', value: p.keyFacts.documents.join(', ') || null },
                  { label: 'Disbursal', value: p.keyFacts.disbursal || null },
                  { label: 'KFS document', value: p.kfsFile ? <code>{p.kfsFile}</code> : <span className="text-muted">lender default</span> },
                  { label: 'Extra key facts', value: p.keyFacts.extra.length ? p.keyFacts.extra.join(' · ') : null },
                ]}
              />
            </div>
            {!p.active && (
              <p className="mt-3 text-2xs text-muted"><Badge tone="warning" className="mr-1">inactive</Badge>Hidden on the website and in the app; its endpoint answers <code>product_unavailable</code>. Workflow and code are kept.</p>
            )}
          </section>
        ))}
      </div>
      <ProductDrawer lender={l} product={editing === 'new' ? null : editing} isOpen={editing !== null} onClose={() => setEditing(null)} />
    </>
  )
}
